import { Dataset } from '~/packages/general/system/bulk/systemBulk.data'
import { parseDateTime, stringifyDateTime } from '~/prix/types/dateTime'
import clearString from '~/prix/utils/bulk/clearString'

import {
  parseCsvCalendarDate,
  parseCsvLocalDateTime,
  parseEnumEmptyString,
  parseBooleanValue,
  parseNullableBooleanValue,
} from '~/prix/utils/bulk/csvValueParsers'

const citizenHealthProfile: Dataset = {
  schema: 'public_health',
  table: 'citizen_health_profile',
  columnTransformMap: {
    original_id: original => original,
    citizen_code: original => original,
    citizen_original_id: original => original,
    cpf: original => original,
    cns: original => original,
    name: original => clearString(original as string),
    has_tuberculosis: original => parseBooleanValue(original as string),
    is_responsible_family_member: original => parseNullableBooleanValue(original as string),
    cpf_responsible_member: original => original,
    cns_responsible_member: original => original,
    mothers_name: original => clearString(original as string),
    fathers_name: original => clearString(original as string),
    birth_date: original => parseCsvCalendarDate(original as string),
    mobile_phone: original => clearString(original as string),
    email: original => clearString(original as string),
    property_type: original => parseEnumEmptyString(original as string),
    micro_area: original => original,
    deceased: original => parseNullableBooleanValue(original as string),
    naturalization_date: original => parseCsvCalendarDate(original as string),
    naturalization_ordinance: original => original,
    date_of_entry_in_brazil: original => parseCsvCalendarDate(original as string),
    last_registration_update: original => parseCsvLocalDateTime(original as string),
    death_date: original => parseCsvCalendarDate(original as string),
    nis: original => original,
    sex: original => parseEnumEmptyString(original as string),
    skin_color_or_race: original => parseEnumEmptyString(original as string),
    nationality: original => parseEnumEmptyString(original as string),
    education_level: original => parseEnumEmptyString(original as string),
    wich_traditional_groups_or_community: original => original,
    gender_identity: original => original,
    which_disability: original => original,
    kinship_with_visited_family_member: original => original,
    reference_maternity_hospital: original => original,
    which_heart_disease: original => original,
    which_kidney_disease: original => original,
    which_respiratory_disease: original => original,
    hospital_internment_reasons: original => original,
    which_medicinal_plants: original => original,
    have_other_health_conditions: original => original,
    food_source: original => original,
    which_access_to_personal_hygiene: original => original,
    assisted_by_which_institution: original => original,
    refused_to_register: original => parseBooleanValue(original as string),
    unknown_mother: original => parseBooleanValue(original as string),
    unknown_father: original => parseBooleanValue(original as string),
    smoker: original => parseBooleanValue(original as string),
    is_pregnant: original => parseBooleanValue(original as string),
    consumes_alcoholic_beverages: original => parseBooleanValue(original as string),
    take_drugs: original => parseBooleanValue(original as string),
    has_hypertension: original => parseBooleanValue(original as string),
    has_diabetes: original => parseBooleanValue(original as string),
    had_stroke: original => parseBooleanValue(original as string),
    had_heart_attack: original => parseBooleanValue(original as string),
    has_heart_disease: original => parseBooleanValue(original as string),
    had_kidney_disease: original => parseBooleanValue(original as string),
    has_respiratory_disease: original => parseBooleanValue(original as string),
    has_leprosy: original => parseBooleanValue(original as string),
    had_cancer: original => parseBooleanValue(original as string),
    had_recent_hospitalization: original => parseBooleanValue(original as string),
    has_medical_professional_diagnosis_of_mental_conditions: original =>
      parseBooleanValue(original as string),
    is_bedridden: original => parseBooleanValue(original as string),
    is_domiciled: original => parseBooleanValue(original as string),
    uses_medicinal_plants: original => parseBooleanValue(original as string),
    makes_use_of_other_integrative_and_complementary_practices: original =>
      parseBooleanValue(original as string),
    receives_benefit: original => parseBooleanValue(original as string),
    homeless: original => parseBooleanValue(original as string),
    visit_family_members_often: original => parseBooleanValue(original as string),
    assisted_by_any_institution: original => parseBooleanValue(original as string),
    attends_alternative_healer: original => parseBooleanValue(original as string),
    take_part_in_community_groups: original => parseBooleanValue(original as string),
    has_private_health_insurance: original => parseBooleanValue(original as string),
    member_of_traditional_groups_or_community: original => parseBooleanValue(original as string),
    wish_to_inform_sexual_orientation: original => parseBooleanValue(original as string),
    has_any_disability: original => parseBooleanValue(original as string),
    have_any_kind_of_family_reference: original => parseBooleanValue(original as string),
    has_access_to_personal_hygiene: original => parseBooleanValue(original as string),
    attends_school_or_daycare: original => parseBooleanValue(original as string),
    wish_to_inform_gender_identity: original => parseBooleanValue(original as string),
    has_gender_identity: original => parseBooleanValue(original as string),
    has_diagnosed_mental_health_condition: original => parseBooleanValue(original as string),
    wants_to_report_sexual_orientation: original => parseBooleanValue(original as string),
    had_food_shortage_lately: original => parseBooleanValue(original as string),
    ate_only_available_food_due_to_lack_of_money: original => parseBooleanValue(original as string),
    current_weight_status: original => parseEnumEmptyString(original as string),
    marital_status: original => parseEnumEmptyString(original as string),
    child_zero_to_nine_years_old_who_takes_care: original =>
      parseEnumEmptyString(original as string),
    eat_how_many_times_day: original => parseEnumEmptyString(original as string),
    homeless_for_how_many_time: original => parseEnumEmptyString(original as string),
    highest_course_attended: original => parseEnumEmptyString(original as string),
    employment_status: original => parseEnumEmptyString(original as string),
    contact_phone: original => original,
    social_name: original => original,
    ethnicity: original => original,
    country_of_birth: original => original,
    birthplace_municipality: original => original,
    birthplace_state: original => original,
    occupation: original => original,
    child_zero_to_nine_years_old: original => parseBooleanValue(original as string),
    sexual_orientation: original => parseEnumEmptyString(original as string),
    death_certificate_number: original => original,
    during_the_last_three_months_had_food_shortage_without_financial_means_to_get_more: original =>
      parseBooleanValue(original as string),
    during_the_last_three_months_ate_only_available_food_because_of_lack_of_money: original =>
      parseBooleanValue(original as string),
    moved: original => parseNullableBooleanValue(original as string),
    during_the_last_6_months_had_any_hospital_internment: original =>
      parseBooleanValue(original as string),
    professional_cns: original => original,
    professional_cnes: original => original,
    professional_cbo: original => original,
    professional_ine: original => original,
    reason_for_leaving_registration: original => parseEnumEmptyString(original as string),
    kinship_with_family_responsible: original => parseEnumEmptyString(original as string),
    created_at: original => stringifyDateTime(parseDateTime(original as string)),
    updated_at: original => stringifyDateTime(parseDateTime(original as string)),
  },
}

export default citizenHealthProfile
